// =============================================================================
// LEGAL PAGES CONTENT (TERMS & PRIVACY)
// =============================================================================

import type { ContentComponent } from '@/types';

export const termsPageComponents: ContentComponent[] = [
  {
    id: 'hero-terms',
    type: 'hero',
    title: 'Terms of Service',
    content: 'Please read these terms carefully before using our services.',
    metadata: {
      image: '/images/back1-brown.jpg',
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'terms-content',
    type: 'text',
    title: 'Terms of Service',
    content: `
      <p><strong>Last updated:</strong> ${new Date().toLocaleDateString()}</p>
      
      <h3>1. Acceptance of Terms</h3>
      <p>By accessing and using the services provided by Awani ("we," "us," or "our"), you accept and agree to be bound by the terms and provision of this agreement.</p>
      
      <h3>2. Description of Service</h3>
      <p>Awani provides luxury alpaca wool clothing and accessories. These services include but are not limited to:</p>
      <ul>
        <li>Handcrafted ponchos in pure Peruvian alpaca wool</li>
        <li>Kimono garments with rare shalina weavings</li>
        <li>Alpaca wool pants</li>
        <li>Hand-stitched leather accessories (holsters and bags)</li>
        <li>Made-to-order custom pieces</li>
        <li>Consultation and design services</li>
      </ul>
      
      <h3>3. Customer Responsibilities</h3>
      <p>As a customer, you agree to:</p>
      <ul>
        <li>Provide accurate sizing and design information for custom orders</li>
        <li>Make payments according to the agreed schedule</li>
        <li>Follow care instructions provided with your garment</li>
        <li>Contact us with any questions or concerns about your order</li>
        <li>Understand that each piece is one-of-a-kind and may have natural variations</li>
      </ul>
      
      <h3>4. Payment Terms</h3>
      <p>Payment terms will be specified for each order. Generally:</p>
      <ul>
        <li>Payment is required to reserve your piece</li>
        <li>Full payment may be required before delivery</li>
        <li>We accept various payment methods for your convenience</li>
        <li>Contact us to discuss payment options</li>
      </ul>
      
      <h3>5. Product Care and Warranty</h3>
      <p>Awani garments are made from pure alpaca wool and require special care. Important considerations:</p>
      <ul>
        <li>All garments are hand-wash only—machine washing will ruin them</li>
        <li>Follow the care instructions provided with your garment</li>
        <li>Natural variations in fiber and color are part of the handmade nature of each piece</li>
        <li>Each piece is one-of-a-kind and may have unique characteristics</li>
      </ul>
      
      <h3>6. Delivery</h3>
      <p>Each piece is custom made to order and will be delivered within 2-4 weeks of purchase. Delivery times may vary based on the complexity of your order and artisan availability. We will keep you informed of your order status throughout the process.</p>
      
      <h3>7. Limitation of Liability</h3>
      <p>Our liability is limited to the amount paid for the specific product. We are not liable for indirect, incidental, or consequential damages, including damage from improper care or washing.</p>
      
      <h3>8. Returns and Exchanges</h3>
      <p>Due to the one-of-a-kind nature of our pieces, returns and exchanges are limited. Returns are possible for minimally acceptable reasons. Please contact Awani to discuss any concerns about your order or to initiate a return. Each piece is unique, so please review details carefully before purchase.</p>
      
      <h3>9. Changes to Terms</h3>
      <p>We reserve the right to modify these terms at any time. Continued use of our services constitutes acceptance of the modified terms.</p>
      
      <h3>10. Contact Information</h3>
      <p>For questions about these terms, please contact us on our <a href="/about#contact-form">contact page</a>.</p>
    `,
    metadata: {
      textAlign: 'left',
      maxWidth: 900
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'shipping',
    type: 'text',
    title: 'Shipping & Returns',
    content: `
      <h3>Shipping</h3>
      <p>Each piece is custom made to order and will be delivered within 2-4 weeks of purchase. Delivery times may vary based on the complexity of your order and artisan availability in the Sacred Valley of Peru.</p>
      
      <p>We will keep you informed of your order status throughout the process. Once your piece is ready, we will coordinate shipping and provide you with tracking information.</p>
      
      <h3>Returns</h3>
      <p>Due to the one-of-a-kind, handcrafted nature of our pieces, returns are possible for minimally acceptable reasons only. Each piece is entirely unique, crafted specifically for you by Peruvian artisans using traditional techniques.</p>
      
      <p>If you have concerns about your order or wish to initiate a return, please contact Awani through our <a href="/about#contact-form">contact form</a>. We will work with you to address any issues and determine the best course of action.</p>
      
      <p>Please note that returns must be requested within a reasonable timeframe and items must be in their original condition, unworn, and with all original packaging and care instructions.</p>
    `,
    metadata: {
      textAlign: 'left',
      maxWidth: 900
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  }
];

export const privacyPageComponents: ContentComponent[] = [
  {
    id: 'hero-privacy',
    type: 'hero',
    title: 'Privacy Policy',
    content: 'Your privacy is important to us. This policy explains how we collect, use, and protect your information.',
    metadata: {
      image: '/images/back2-black.jpg',
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: 'privacy-content',
    type: 'text',
    title: 'Privacy Policy',
    content: `
      <p><strong>Last updated:</strong> ${new Date().toLocaleDateString()}</p>
      
      <h3>1. Information We Collect</h3>
      <p>We collect information you provide directly to us, such as when you:</p>
      <ul>
        <li>Contact us through our website or email</li>
        <li>Place an order for garments or accessories</li>
        <li>Request information about our collections</li>
        <li>Subscribe to our newsletter or updates</li>
      </ul>
      
      <h3>2. Types of Information</h3>
      <p>The information we collect may include:</p>
      <ul>
        <li>Personal information (name, email address, phone number, shipping address)</li>
        <li>Order information (sizing, design preferences, custom order details)</li>
        <li>Communication records (emails, order inquiries)</li>
        <li>Technical information (browser type, IP address for analytics)</li>
      </ul>
      
      <h3>3. How We Use Your Information</h3>
      <p>We use the information we collect to:</p>
      <ul>
        <li>Process and fulfill your orders</li>
        <li>Communicate with you about your orders and our collections</li>
        <li>Send you relevant updates and information about new pieces</li>
        <li>Analyze website usage and improve user experience</li>
        <li>Comply with legal obligations</li>
      </ul>
      
      <h3>4. Information Sharing</h3>
      <p>We do not sell, trade, or otherwise transfer your personal information to third parties without your consent, except:</p>
      <ul>
        <li>To trusted service providers who assist in our operations</li>
        <li>When required by law or to protect our rights</li>
        <li>In connection with a business transfer or acquisition</li>
      </ul>
      
      <h3>5. Data Security</h3>
      <p>We implement appropriate security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction.</p>
      
      <h3>6. Data Retention</h3>
      <p>We retain your information for as long as necessary to provide our services and comply with legal obligations.</p>
      
      <h3>7. Your Rights</h3>
      <p>You have the right to:</p>
      <ul>
        <li>Access your personal information</li>
        <li>Correct inaccurate information</li>
        <li>Request deletion of your information</li>
        <li>Opt out of marketing communications</li>
      </ul>
      
      <h3>8. Cookies and Tracking</h3>
      <p>Our website may use cookies and similar tracking technologies to enhance your experience and analyze usage patterns.</p>
      
      <h3>9. Third-Party Services</h3>
      <p>We may use third-party services (such as Google Analytics) that have their own privacy policies. We encourage you to review these policies.</p>
      
      <h3>10. Changes to This Policy</h3>
      <p>We may update this privacy policy from time to time. We will notify you of any changes by posting the new policy on this page.</p>
      
      <h3>11. Contact Us</h3>
      <p>If you have questions about this privacy policy, please contact us on our <a href="/contact">contact page</a>.</p>
    `,
    metadata: {
      textAlign: 'left',
      maxWidth: 900
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  }
];

export const notFoundPageComponents: ContentComponent[] = [
  {
    id: 'hero-404',
    type: 'hero',
    title: '404 - Page Not Found',
    content: 'The page you\'re looking for doesn\'t exist or has been moved.',
    metadata: {
      image: '/images/back1-black.jpg',
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  },
  {
    id: '404-content',
    type: 'text',
    title: 'Oops! We couldn\'t find that page',
    content: `
      <p>It looks like the page you're trying to reach doesn't exist. This could be because:</p>
      <ul>
        <li>The URL was typed incorrectly</li>
        <li>The page has been moved or deleted</li>
        <li>The link you followed is outdated</li>
      </ul>
      
      <h3>What can you do?</h3>
      <p>Here are some helpful links to get you back on track:</p>
      <ul>
        <li><a href="/">Go to Homepage</a> - Start from the beginning</li>
        <li><a href="/collections">View Collections</a> - See our pieces</li>
        <li><a href="/collections">Browse Collections</a> - Check out our garments</li>
        <li><a href="/about">About Us</a> - Learn more about Awani</li>
        <li><a href="/contact">Contact Us</a> - Get in touch with us</li>
      </ul>
      
      <p>If you believe this is an error or if you continue to experience issues, please <a href="/contact">contact us</a> and let us know.</p>
    `,
    metadata: {
      textAlign: 'left',
      maxWidth: 900
    },
    createdAt: new Date(),
    updatedAt: new Date(),
  }
];
